// Works in Service Worker (no window/localStorage)
// Minimal, dependency-free IndexedDB adapter for redux-persist.

const DB_NAME = 'redux-persist';
const STORE_NAME = 'keyval';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
  });
}

const storage = {
  // redux-persist expects Promises
  getItem(key: string): Promise<string | null> {
    return withStore('readonly', (s) => s.get(key)).then(
      (v: any) => (v === undefined ? null : (v as string))
    );
  },
  setItem(key: string, value: string): Promise<void> {
    // value should be a string; redux-persist passes serialized state
    return withStore('readwrite', (s) => s.put(value, key)).then(() => {});
  },
  removeItem(key: string): Promise<void> {
    return withStore('readwrite', (s) => s.delete(key)).then(() => {});
  },
  // Some libs call this; nice to have and harmless
  getAllKeys(): Promise<string[]> {
    return withStore('readonly', (s) => s.getAllKeys()).then((keys) =>
      (keys as IDBValidKey[]).map((k) => String(k))
    );
  },
};

export default storage;
